import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { getAccount, reconnect } from '@wagmi/core';
import type { Config } from '@wagmi/core';
import { tryGetWagmiConfig } from '../utils/wagmi-client';
import { WagmiConfigService } from './wagmi-config.service';

/**
 * Restores the wallet connection when the tab returns to the foreground.
 *
 * @remarks
 * Connecting on mobile means leaving the browser entirely: the dApp hands off to MetaMask, and
 * Android Chrome is free to freeze — or under memory pressure discard — the backgrounded tab
 * while that happens. A frozen tab has its timers suspended and its WebSocket to the
 * WalletConnect relay torn down, so the tab that comes back is not the tab that left. wagmi's
 * connectors reattach on their own eventually, but nothing re-drives the app's view of them and
 * nothing reopens the relay transport, which is how a connection that succeeded in the wallet
 * still reads as "hidden"/paused back in the browser.
 *
 * iOS Safari and desktop suspend far less aggressively, which is why this only shows up on
 * Android.
 *
 * Everything here is read from wagmi's live in-memory state. Nothing polls
 * `localStorage['wagmi.store']`, and nothing runs behind a `setTimeout`: on a thawing tab the
 * timer fires against a store that has not finished rehydrating, and reads back a stale — or
 * absent — account.
 */
@Injectable({
  providedIn: 'root'
})
export class WalletResumeService implements OnDestroy {
  private started = false;
  private resumeInFlight = false;
  private lastResumeAt = 0;

  /**
   * Foreground events arrive in bursts — `visibilitychange` and `focus` typically fire together
   * on the same return — and one resume covers the burst.
   */
  private static readonly RESUME_THROTTLE_MS = 750;

  /**
   * Ceiling on any single network step. A relay socket killed by a freeze does not fail fast,
   * it hangs, and an un-timed `await` would strand the resume and every later one behind it.
   */
  private static readonly STEP_TIMEOUT_MS = 8_000;

  constructor(
    private readonly wagmiConfigService: WagmiConfigService,
    private readonly zone: NgZone
  ) {}

  /**
   * Begins listening for foreground events. Called once at app bootstrap, after
   * `wagmiConfiguration()`.
   * @returns {void}
   */
  public start(): void {
    if (this.started || typeof document === 'undefined') return;
    this.started = true;

    // Three listeners because no one of them covers every way an Android tab comes back:
    //
    // - `visibilitychange` is the main path (app switcher, notification shade, screen unlock).
    // - `pageshow` with `persisted` is the back/forward cache: the tab is restored wholesale
    //   from a snapshot, so no visibility transition is reported and this is the only signal.
    // - `focus` catches the window regaining input without a visibility change, which is what
    //   returning from a custom-tab or an overlaid wallet intent tends to produce.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('focus', this.onFocus);
  }

  public ngOnDestroy(): void {
    if (!this.started) return;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('focus', this.onFocus);
    this.started = false;
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    void this.resume();
  };

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    // A non-persisted `pageshow` is an ordinary load, which wagmi's own autoconnect already
    // covers. Only a bfcache restore needs healing here.
    if (!event.persisted) return;
    void this.resume();
  };

  private readonly onFocus = (): void => {
    void this.resume();
  };

  /**
   * Re-establishes the app's view of the wallet after a foreground event.
   *
   * @remarks
   * Ordered deliberately: the synchronous re-emit lands first so the UI is correct within the
   * same task as the resume, before any awaited work gets a chance to be frozen again.
   * @returns a promise that resolves once the connection has been re-driven
   */
  private async resume(): Promise<void> {
    const config = tryGetWagmiConfig();
    if (!config) return;

    // Single-flight: a slow reconnect must not stack behind a second foreground event.
    if (this.resumeInFlight) return;

    const now = Date.now();
    if (now - this.lastResumeAt < WalletResumeService.RESUME_THROTTLE_MS) return;
    this.lastResumeAt = now;
    this.resumeInFlight = true;

    try {
      // Step 1, synchronous. Re-emit whatever wagmi holds right now, so a component that
      // rebuilt itself over the freeze gets the current account immediately rather than
      // waiting on the network.
      this.emitInZone();

      const status = getAccount(config).status;

      if (status === 'connecting' || status === 'reconnecting') {
        // wagmi is already mid-handshake. Driving `reconnect()` into it would cancel the
        // in-flight attempt and restart the very race this exists to avoid.
        return;
      }

      if (status === 'connected') {
        // Connected on paper. The relay socket may still be dead from the freeze, so signing
        // or sending would hang with no visible cause.
        await this.healRelayTransport(config);
        return;
      }

      // Disconnected. `reconnect()` restores from wagmi's persisted connector list without any
      // wallet prompt when the session is still valid, and no-ops when there is nothing to
      // restore — safe to call unconditionally on a genuinely logged-out visitor.
      await this.withTimeout(reconnect(config));

      // Re-emit: `reconnect()` resolving means the store has settled.
      this.emitInZone();
    } catch {
      // A failed resume must never surface as an error to the user: they may simply not have a
      // wallet connected. The next foreground event retries.
    } finally {
      this.resumeInFlight = false;
    }
  }

  /**
   * Reopens the WalletConnect relay WebSocket when the freeze took it down.
   *
   * @remarks
   * The relayer reconnects on its own on some paths, but not reliably after a freeze: the
   * socket is closed without a close frame reaching the page, so the client can still believe
   * it is connected. Requests then queue against a dead transport and hang instead of failing.
   * `restartTransport()` tears the socket down and dials again from scratch.
   *
   * Reached through the provider's internals, which are not part of the public typing, so every
   * hop is guarded and the whole thing is best-effort — a failure here leaves the connection no
   * worse than the freeze already did.
   * @param config the wagmi config
   * @returns a promise that resolves once the transport has been re-dialled, or immediately
   */
  private async healRelayTransport(config: Config): Promise<void> {
    const connector = getAccount(config).connector;
    // Injected/browser-extension connectors have no relay to heal.
    if (!connector?.id?.toLowerCase().includes('walletconnect')) return;

    const provider: any = await this.withTimeout(
      Promise.resolve(connector.getProvider())
    ).catch(() => undefined);

    // EthereumProvider -> UniversalProvider (`signer`) -> SignClient (`client`) -> Core.
    const relayer = provider?.signer?.client?.core?.relayer;
    if (!relayer) return;

    if (relayer.connected === true) return;

    if (typeof relayer.restartTransport === 'function') {
      await this.withTimeout(Promise.resolve(relayer.restartTransport()));
    } else if (typeof relayer.transportOpen === 'function') {
      // Older relayer builds expose only the lower-level open.
      await this.withTimeout(Promise.resolve(relayer.transportOpen()));
    }
  }

  /**
   * Re-emits current account state inside Angular's zone.
   *
   * @remarks
   * The listeners can fire on a callback zone.js did not patch — notably after a bfcache
   * restore — in which case an emission would update the subject without scheduling change
   * detection, and the view would stay on its pre-freeze render. Running the emit explicitly
   * inside the zone makes that deterministic.
   * @returns {void}
   */
  private emitInZone(): void {
    this.zone.run(() => this.wagmiConfigService.syncAccountFromWagmi());
  }

  /**
   * Rejects if the given promise has not settled within {@link STEP_TIMEOUT_MS}.
   * @param work the promise to bound
   * @returns the promise's value
   */
  private withTimeout<T>(work: Promise<T>): Promise<T> {
    return Promise.race([
      work,
      new Promise<T>((_resolve, reject) =>
        setTimeout(
          () => reject(new Error('Wallet resume step timed out')),
          WalletResumeService.STEP_TIMEOUT_MS
        )
      )
    ]);
  }
}
